import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useScrollPosition } from '../../hooks';
import { useLanguage, type Language } from '../../i18n';
import { cn } from '../../utils';
import { FiDownload, FiGlobe } from 'react-icons/fi';
import { CVGenerator } from '../common';
import styles from './Header.module.css';
import logoImage from '../../assets/logo.png';

export function Header() {
  const scrollPosition = useScrollPosition();
  const isScrolled = scrollPosition > 50;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showCV, setShowCV] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();

  const NAV_ITEMS = [
    { label: t.nav.home, href: '/#home' },
    { label: t.nav.about, href: '/#about-me' },
    { label: t.nav.services, href: '/#what-i-do' },
    { label: t.nav.projects, href: '/#portfolio' },
    { label: t.nav.experience, href: '/#my-resume' },
    { label: t.nav.playground, href: '/#playground' },
    { label: t.nav.contact, href: '/#contact' },
  ];

  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang);
    setShowLangMenu(false);
  };

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const handleNavClick = () => {
    setIsMenuOpen(false);
  };

  const handleCVClick = () => {
    setIsMenuOpen(false);
    setShowCV(true);
  };

  return (
    <header className={cn(styles.header, isScrolled && styles.scrolled, isMenuOpen && styles.menuOpen)}>
      <nav className={styles.nav}>
        <Link to="/" className={styles.logo}>
          <div className={styles.logoGlitch}>
            <img src={logoImage} alt="Fernando Olea" className={styles.logoImage} />
            <img src={logoImage} alt="" className={styles.logoGlitchLayer} aria-hidden="true" />
            <img src={logoImage} alt="" className={styles.logoGlitchLayer} aria-hidden="true" />
          </div>
        </Link>

        <ul className={cn(styles.navList, isMenuOpen && styles.navListOpen)}>
          {NAV_ITEMS.map((item) => (
            <li key={item.href}>
              <a href={item.href} className={styles.navLink} onClick={handleNavClick}>
                {item.label}
              </a>
            </li>
          ))}
          <li>
            <button className={styles.cvButton} onClick={handleCVClick}>
              <FiDownload className={styles.cvIcon} />
              <span>CV</span>
            </button>
          </li>
          <li className={styles.langSwitcher}>
            <button
              className={styles.langButton}
              onClick={() => setShowLangMenu(!showLangMenu)}
              aria-label="Change language"
            >
              <FiGlobe className={styles.langIcon} />
              <span className={styles.langCode}>{language.toUpperCase()}</span>
            </button>
            {showLangMenu && (
              <div className={styles.langMenu}>
                <button
                  className={cn(styles.langOption, language === 'es' && styles.langOptionActive)}
                  onClick={() => handleLanguageChange('es')}
                >
                  <span className={styles.langFlag}>ES</span>
                  <span>Español</span>
                </button>
                <button
                  className={cn(styles.langOption, language === 'en' && styles.langOptionActive)}
                  onClick={() => handleLanguageChange('en')}
                >
                  <span className={styles.langFlag}>EN</span>
                  <span>English</span>
                </button>
              </div>
            )}
          </li>
        </ul>

        <button
          className={cn(styles.menuButton, isMenuOpen && styles.menuButtonOpen)}
          aria-label={isMenuOpen ? t.nav.closeMenu : t.nav.openMenu}
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <span className={styles.menuIcon} />
        </button>
      </nav>

      {/* CV Generator Modal */}
      {showCV && <CVGenerator onClose={() => setShowCV(false)} />}
    </header>
  );
}
